import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Ship, Hotel, CalendarDays, MapPin, Check, Users, Package as PackageIcon, Sparkles, ShieldCheck, Wine, Car, Compass, UtensilsCrossed, MoreHorizontal, Loader2, ArrowRight, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtMoney, priceForTravelers, pkgFromPrice, pkgTiers, fmtShort, addDays } from "@/lib/trip";
import { cn } from "@/lib/utils";

const ADDON_ICON = { "Travel Insurance": ShieldCheck, "Drink Package": Wine, "Transfer": Car, "Excursion": Compass, "Specialty Dining": UtensilsCrossed, "Other": MoreHorizontal };
const money = (n) => fmtMoney(n, !Number.isInteger(Number(n)));
const field = "h-12 w-full rounded-xl border border-white/15 bg-white/5 px-4 text-base text-white outline-none transition-colors placeholder:text-white/30 focus:border-amber-300/60 focus:bg-white/10";
const lbl = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-white/50";

function Shell({ children }) {
  return (
    <div className="scroll-area fixed inset-0 bg-navy-950 text-white" data-testid="group-public-page">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <a href="/" className="flex items-center"><img src="/images/topsail-mark.png" alt="Topsail Travel" className="h-12 w-auto object-contain sm:h-14" data-testid="public-topsail-logo" /></a>
          <a href="/portal" className="rounded-full border border-white/20 px-4 py-1.5 text-sm font-medium text-white/80 transition-colors hover:border-white/50 hover:text-white" data-testid="public-mytrip-link">MyTrip Portal</a>
        </div>
      </header>
      {children}
    </div>
  );
}

function StateMsg({ icon: Icon, title, text }) {
  return (
    <Shell>
      <div className="mx-auto grid max-w-md place-items-center px-6 py-32 text-center">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-white/5"><Icon className="h-7 w-7 text-white/60" /></span>
        <h1 className="mt-5 font-display text-2xl font-bold">{title}</h1>
        <p className="mt-2 text-white/60">{text}</p>
      </div>
    </Shell>
  );
}

export default function GroupPublic() {
  const { code } = useParams();
  const [g, setG] = useState(null);
  const [state, setState] = useState("loading");
  const [pkgId, setPkgId] = useState("");
  const [addonIds, setAddonIds] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", travelers: 1, agentId: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [detailPkg, setDetailPkg] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get(`/public/groups/${code}`)
      .then(({ data }) => { if (alive) { setG(data); setState("ok"); } })
      .catch((e) => { if (alive) setState(e?.response?.status === 404 ? "missing" : "error"); });
    return () => { alive = false; };
  }, [code]);

  const pkg = useMemo(() => (g?.packages || []).find((p) => p.id === pkgId) || null, [g, pkgId]);
  const selAddons = useMemo(() => (g?.addons || []).filter((a) => addonIds.includes(a.id)), [g, addonIds]);
  const travelers = Math.max(1, Number(form.travelers) || 1);

  const addonCost = (a) => (a.pricing === "per_person" ? Number(a.price || 0) * travelers : Number(a.price || 0));
  const addonsTotal = selAddons.reduce((s, a) => s + addonCost(a), 0);
  const addonsNow = selAddons.filter((a) => a.timing === "immediate").reduce((s, a) => s + addonCost(a), 0);
  const pkgTotal = pkg ? priceForTravelers(pkg, travelers) * travelers : 0;
  const depositNow = g ? (g.depositMode === "per_person" ? Number(g.depositAmount || 0) * travelers : Number(g.depositAmount || 0)) : 0;
  const dueNow = depositNow + addonsNow;
  const estTotal = pkgTotal + addonsTotal;

  const toggleAddon = (id) => setAddonIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (!/.+@.+\..+/.test(form.email)) return toast.error("Please enter a valid email");
    if (!form.agentId) return toast.error("Please choose an advisor");
    setSubmitting(true);
    try {
      await api.post(`/public/groups/${code}/book`, {
        agentId: form.agentId, name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        travelers, packageId: pkgId, addonIds, note: form.note.trim(),
      });
      const advisor = (g.agents || []).find((a) => a.id === form.agentId);
      setDone({ advisor: advisor?.name || "your advisor", package: pkg, pkgPrice: pkg ? priceForTravelers(pkg, travelers) : 0, addons: selAddons, travelers, dueNow, estTotal });
    } catch (e) { toast.error(errMsg(e)); }
    setSubmitting(false);
  };

  if (state === "loading") return <StateMsg icon={Loader2} title="Loading…" text="Fetching this group experience." />;
  if (state === "missing") return <StateMsg icon={PackageIcon} title="Group not available" text="This group page may be unpublished or the link is incorrect." />;
  if (state === "error") return <StateMsg icon={PackageIcon} title="Something went wrong" text="Please refresh the page or try again shortly." />;

  const TypeIcon = g.type === "Resort" ? Hotel : Ship;
  const dates = [g.departure, g.returnDate].filter(Boolean).map((d) => fmtDate(d)).join(" – ");

  if (done) {
    return (
      <Shell>
        <div className="mx-auto max-w-xl px-6 py-16" data-testid="group-booking-confirmation">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500/15 text-emerald-300"><Check className="h-7 w-7" /></span>
          <h1 className="mt-5 font-display text-3xl font-bold">Request sent!</h1>
          <p className="mt-2 text-white/70">Thanks, {form.name.split(" ")[0]}. <strong className="text-white">{done.advisor}</strong> will reach out shortly to confirm your booking for <strong className="text-white">{g.name}</strong>.</p>
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/50">Your request</h2>
            <dl className="mt-4 space-y-2.5 text-sm">
              <Row k="Travelers" v={`${done.travelers}`} />
              {done.package && <Row k="Package" v={`${done.package.name} · ${money(done.pkgPrice)}/person`} />}
              {done.addons.map((a) => <Row key={a.id} k="Add-on" v={a.type === "Other" ? a.otherLabel : a.type} />)}
              <div className="my-2 border-t border-white/10" />
              <Row k="Estimated total" v={money(done.estTotal)} strong />
              <Row k="Due at booking" v={money(done.dueNow)} accent />
            </dl>
          </div>
          <div className="mt-6 rounded-2xl border border-amber-300/20 bg-amber-300/5 p-5 text-sm text-white/70">
            <p><strong className="text-amber-200">MyTrip access:</strong> Once your advisor confirms this booking, they'll set up your MyTrip account so you can track payments, documents and messages in one place. You'll receive an activation link by email.</p>
            <a href="/portal" className="mt-3 inline-flex items-center gap-1.5 font-medium text-amber-200 hover:text-amber-100">Visit MyTrip Portal <ArrowRight className="h-4 w-4" /></a>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="relative overflow-hidden">
        {g.cover
          ? <img src={g.cover} alt="" referrerPolicy="no-referrer" className="absolute inset-0 h-full w-full object-cover" />
          : <div className="absolute inset-0 bg-gradient-to-br from-navy-800 to-navy-950" />}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/70 to-navy-950/30" />
        <div className="relative mx-auto max-w-6xl px-5 pb-10 pt-20 sm:pt-28">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium backdrop-blur"><TypeIcon className="h-3.5 w-3.5" />{g.type} Group</span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight sm:text-5xl" data-testid="public-group-name">{g.name}</h1>
          <p className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-white/75">
            {g.destination && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{g.destination}</span>}
            {dates && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{dates}</span>}
            {g.vessel && <span className="inline-flex items-center gap-1.5"><TypeIcon className="h-4 w-4" />{g.vessel}</span>}
          </p>
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 lg:grid-cols-[1fr_380px]">
        <div className="space-y-12">
          {g.description && (
            <section>
              <p className="whitespace-pre-line text-lg leading-relaxed text-white/75">{g.description}</p>
            </section>
          )}

          <section data-testid="public-packages">
            <h2 className="font-display text-2xl font-bold">Choose your package</h2>
            <p className="mt-1 text-white/55">Select the cabin or room type that suits you.</p>
            {(g.packages || []).length ? (
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {g.packages.map((p) => {
                  const active = pkgId === p.id;
                  return (
                    <button key={p.id} type="button" onClick={() => setDetailPkg(p)} data-testid={`public-package-${p.id}`}
                      className={cn("group overflow-hidden rounded-2xl border text-left transition-all", active ? "border-amber-300 ring-2 ring-amber-300/40" : "border-white/10 hover:border-white/30")}>
                      <div className="relative aspect-[3/2] overflow-hidden bg-white/5">
                        {p.cover ? <img src={p.cover} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="grid h-full place-items-center text-white/20"><PackageIcon className="h-9 w-9" /></div>}
                        {active && <span className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full bg-amber-300 text-navy-950"><Check className="h-4 w-4" /></span>}
                      </div>
                      <div className="p-4">
                        <h3 className="font-display text-lg font-bold">{p.name}</h3>
                        {p.description && <p className="mt-1 line-clamp-2 text-sm text-white/55">{p.description}</p>}
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-lg font-bold text-amber-200">from {money(pkgFromPrice(p))}<span className="text-sm font-normal text-white/50"> /person</span></span>
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-white/60 transition-colors group-hover:text-amber-200">{active ? "Selected" : "View Details"}<ArrowRight className="h-3.5 w-3.5" /></span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : <p className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6 text-white/55">Packages will be announced soon.</p>}
          </section>

          {(g.addons || []).length > 0 && (
            <section data-testid="public-addons">
              <h2 className="font-display text-2xl font-bold">Enhance your trip</h2>
              <p className="mt-1 text-white/55">Optional extras you can add to your booking.</p>
              <div className="mt-6 space-y-3">
                {g.addons.map((a) => {
                  const Icon = ADDON_ICON[a.type] || Sparkles;
                  const active = addonIds.includes(a.id);
                  const name = a.type === "Other" ? a.otherLabel : a.type;
                  return (
                    <button key={a.id} type="button" onClick={() => toggleAddon(a.id)} data-testid={`public-addon-${a.id}`}
                      className={cn("flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition-all", active ? "border-amber-300 bg-amber-300/5" : "border-white/10 hover:border-white/30")}>
                      <span className={cn("mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-xl", active ? "bg-amber-300 text-navy-950" : "bg-white/10 text-white/70")}>{active ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="font-semibold">{name}</span>
                          <span className="shrink-0 font-semibold text-amber-200">{money(a.price)}<span className="text-xs font-normal text-white/50">{a.pricing === "per_person" ? " /person" : ""}</span></span>
                        </div>
                        {a.description && <p className="mt-0.5 text-sm text-white/55">{a.description}</p>}
                        <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-medium text-white/60" data-testid={`public-addon-timing-${a.id}`}>{a.timing === "immediate" ? "Due at booking" : "Due later"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {(g.days || []).length > 0 && (
            <section data-testid="public-itinerary">
              <h2 className="font-display text-2xl font-bold">Itinerary</h2>
              <div className="mt-6 space-y-2">
                {g.days.map((d, i) => (
                  <div key={d.id || i} className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4">
                    <span className="grid h-12 w-20 shrink-0 place-items-center gap-0.5 rounded-lg bg-white/10 px-1 text-amber-200">
                      <span className="text-xs font-semibold uppercase tracking-wider">Day {i + 1}</span>
                      {g.departure && <span className="text-[10px] font-medium text-white/60" data-testid={`public-day-date-${i}`}>{fmtShort(addDays(g.departure, i))}</span>}
                    </span>
                    <span className="text-white/80">{d.title || "—"}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:sticky lg:top-6 lg:self-start" ref={formRef}>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur" data-testid="public-booking-panel">
            <h2 className="font-display text-xl font-bold">Request to book</h2>
            <p className="mt-1 text-sm text-white/55">No payment now — your advisor confirms availability first.</p>

            <dl className="mt-5 space-y-2 border-y border-white/10 py-4 text-sm">
              <Row k="Package" v={pkg ? pkg.name : "Not selected"} muted={!pkg} />
              <Row k="Travelers" v={`${travelers}`} />
              {selAddons.length > 0 && <Row k="Add-ons" v={`${selAddons.length} selected`} />}
              <div className="my-1 border-t border-white/5" />
              <Row k="Estimated total" v={money(estTotal)} strong />
              <Row k="Due at booking" v={money(dueNow)} accent />
            </dl>

            <div className="mt-5 space-y-3">
              <div><label className={lbl}>Full name</label><input className={field} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="Your name" data-testid="book-name" /></div>
              <div><label className={lbl}>Email</label><input type="email" className={field} value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="you@email.com" data-testid="book-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lbl}>Phone</label><input className={field} value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} placeholder="Optional" data-testid="book-phone" /></div>
                <div><label className={lbl}>Travelers</label><input type="number" min="1" max="20" className={field} value={form.travelers} onChange={(e) => setForm((p) => ({ ...p, travelers: e.target.value }))} data-testid="book-travelers" /></div>
              </div>
              <div><label className={lbl}>Your advisor</label>
                <select className={cn(field, "appearance-none")} value={form.agentId} onChange={(e) => setForm((p) => ({ ...p, agentId: e.target.value }))} data-testid="book-advisor">
                  <option value="" className="text-navy-950">Choose an advisor…</option>
                  {(g.agents || []).map((a) => <option key={a.id} value={a.id} className="text-navy-950">{a.name}{a.title ? ` · ${a.title}` : ""}</option>)}
                </select>
              </div>
              <div><label className={lbl}>Notes (optional)</label><textarea className={cn(field, "h-20 resize-none py-3")} value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} placeholder="Anything we should know?" data-testid="book-note" /></div>
            </div>

            <button onClick={submit} disabled={submitting} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-300 px-5 py-3.5 font-semibold text-navy-950 transition-colors hover:bg-amber-200 disabled:opacity-60" data-testid="book-submit">
              {submitting ? <><Loader2 className="h-5 w-5 animate-spin" />Sending…</> : <>Request to book<ArrowRight className="h-5 w-5" /></>}
            </button>
          </div>
        </aside>
      </div>
      {detailPkg && <PackageDetailModal pkg={detailPkg} selected={pkgId === detailPkg.id} onSelect={setPkgId} onClose={() => setDetailPkg(null)} />}
    </Shell>
  );
}

function PackageDetailModal({ pkg, selected, onSelect, onClose }) {
  const tiers = pkgTiers(pkg);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6" onClick={onClose} data-testid="package-detail-modal">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-white/10 bg-navy-900 sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative aspect-[3/2] overflow-hidden bg-white/5">
          {pkg.cover ? <img src={pkg.cover} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-white/20"><PackageIcon className="h-12 w-12" /></div>}
          <button onClick={onClose} className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-black/40 text-white backdrop-blur transition-colors hover:bg-black/60" data-testid="package-detail-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6">
          <h3 className="font-display text-2xl font-bold">{pkg.name}</h3>
          <p className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-white/55"><Users className="h-4 w-4" />{pkg.capacity || 0} spots available</p>
          {pkg.description && <p className="mt-4 whitespace-pre-line leading-relaxed text-white/70">{pkg.description}</p>}
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/50">Per-person pricing</p>
            <div className="mt-3 space-y-2.5">
              {tiers.map((t) => (
                <div key={t.travelers} className="flex items-center justify-between text-sm" data-testid={`package-detail-tier-${t.travelers}`}>
                  <span className="text-white/70">{t.travelers === 1 ? "Solo traveler" : `${t.travelers} travelers`}</span>
                  <span className="font-semibold text-amber-200">{money(t.price)}<span className="text-xs font-normal text-white/50"> /person</span></span>
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => { onSelect(pkg.id); onClose(); }} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-300 px-5 py-3.5 font-semibold text-navy-950 transition-colors hover:bg-amber-200" data-testid="package-detail-select">
            {selected ? <>Selected<Check className="h-5 w-5" /></> : <>Select this package<ArrowRight className="h-5 w-5" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, strong, accent, muted }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-white/50">{k}</dt>
      <dd className={cn("text-right", accent ? "font-bold text-amber-200" : strong ? "font-semibold text-white" : muted ? "text-white/40" : "text-white/80")}>{v}</dd>
    </div>
  );
}
